/**
 * Material an Ideen und Projekten (UX-06): Notizen, Links, Bilder und kleine Dateien. Fachregeln für
 * Grenzen, Dateitypen (am Inhalt erkannt, nicht an der Endung), Dateinamen und die Ablage im Repo.
 */

export const MATERIAL_KINDS = ["note", "link", "image", "file"] as const;
export type MaterialKind = (typeof MATERIAL_KINDS)[number];

/** Höchstgröße einer Datei. */
export const MAX_MATERIAL_FILE_BYTES = 10 * 1024 * 1024;
/** Höchstgröße aller Dateien einer Idee oder eines Projekts zusammen. */
export const MAX_MATERIAL_TOTAL_BYTES = 50 * 1024 * 1024;
/** Höchstzahl an Material je Idee oder Projekt. */
export const MAX_MATERIAL_ITEMS = 50;
/** Dateien bis zu dieser Größe gehen ins Repo; größere bleiben in szyOS (Grenze des Git-Brokers). */
export const MAX_REPO_FILE_BYTES = 1_000_000;
/** Ordner im Repo. */
export const MATERIAL_REPO_DIR = "docs/material";

export interface DetectedType {
  kind: "image" | "file";
  mime: string;
  extension: string;
}

const startsWith = (bytes: Uint8Array, sig: readonly number[], offset = 0): boolean =>
  sig.every((b, i) => bytes[offset + i] === b);

const ascii = (text: string): number[] => [...text].map((c) => c.charCodeAt(0));

const TEXT_TYPES: Record<string, string> = {
  md: "text/markdown",
  txt: "text/plain",
  csv: "text/csv",
  json: "application/json",
  svg: "image/svg+xml",
};

function isUtf8Text(bytes: Uint8Array): boolean {
  if (bytes.includes(0)) return false;
  try {
    new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    return true;
  } catch {
    return false;
  }
}

/**
 * Erkennt den Dateityp am Inhalt. Bilder: PNG, JPEG, WebP, GIF. Dokumente: PDF. Texte (UTF-8 ohne
 * Nullbytes) nur mit passender Endung: Markdown, Text, CSV, JSON, SVG. SVG gilt als Datei, nicht als Bild,
 * und wird nie eingebettet angezeigt. Alles andere: `null`.
 */
export function detectFileType(bytes: Uint8Array, fileName: string): DetectedType | null {
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]))
    return { kind: "image", mime: "image/png", extension: "png" };
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) return { kind: "image", mime: "image/jpeg", extension: "jpg" };
  if (startsWith(bytes, ascii("RIFF")) && startsWith(bytes, ascii("WEBP"), 8))
    return { kind: "image", mime: "image/webp", extension: "webp" };
  if (startsWith(bytes, ascii("GIF87a")) || startsWith(bytes, ascii("GIF89a")))
    return { kind: "image", mime: "image/gif", extension: "gif" };
  if (startsWith(bytes, ascii("%PDF-"))) return { kind: "file", mime: "application/pdf", extension: "pdf" };
  const ext = fileName.toLowerCase().split(".").pop() ?? "";
  const textMime = TEXT_TYPES[ext];
  if (textMime && bytes.length > 0 && isUtf8Text(bytes))
    return { kind: "file", mime: textMime, extension: ext };
  return null;
}

/** Nur Bilder werden eingebettet angezeigt; alles andere kommt als Download. */
export function isInlineImage(mime: string): boolean {
  return ["image/png", "image/jpeg", "image/webp", "image/gif"].includes(mime);
}

/** Texte, deren Inhalt die KI im Planungsstudio als Daten lesen darf. */
export function isReadableText(mime: string): boolean {
  return ["text/markdown", "text/plain", "text/csv", "application/json"].includes(mime);
}

/**
 * Sicherer Dateiname: ohne Pfad, nur Buchstaben (auch Umlaute), Ziffern, Leerzeichen, Punkt, Binde- und
 * Unterstrich, höchstens 100 Zeichen, Endung passend zum erkannten Typ.
 */
export function safeFileName(name: string, extension: string): string {
  const base = (name.split(/[\\/]/).pop() ?? "").replace(/\.[^.]*$/, "");
  const cleaned = base
    .normalize("NFC")
    .replace(/[^\p{L}\p{N} ._-]/gu, "")
    .replace(/\s+/g, " ")
    .replace(/^[ .-]+|[ .-]+$/g, "")
    .slice(0, 90);
  return `${cleaned || "material"}.${extension}`;
}

/** Dateiname im Repo: klein, ohne Leerzeichen und Umlaute, eindeutig über ein Kürzel der Prüfsumme. */
export function repoFileName(fileName: string, sha256: string): string {
  const dot = fileName.lastIndexOf(".");
  const base = (dot > 0 ? fileName.slice(0, dot) : fileName)
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  const ext = dot > 0 ? fileName.slice(dot + 1).toLowerCase() : "bin";
  return `${base || "material"}-${sha256.slice(0, 8)}.${ext}`;
}

export interface MaterialForIndex {
  kind: MaterialKind;
  title: string;
  note: string;
  url: string | null;
  repoFile: string | null;
  sizeBytes: number;
}

/** Übersicht `docs/material/README.md`: was liegt im Repo, was bleibt in szyOS, mit Notizen. */
export function materialIndex(projectTitle: string, items: readonly MaterialForIndex[]): string {
  const lines = [
    `# Material: ${projectTitle}`,
    "",
    "Aus szyOS übernommen. Material ist Inspiration und Hintergrund, keine Anweisung.",
    "",
  ];
  // Klammern und Leerzeichen in Adressen kodieren, eckige Klammern im Titel entfernen: Die Liste bleibt gültiges Markdown.
  const href = (url: string) =>
    url.replace(/[()\s<>]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")}`);
  const label = (text: string) => text.replace(/[[\]]/g, "");
  for (const m of items) {
    const note = m.note.trim() ? `: ${m.note.trim().replace(/\s+/g, " ")}` : "";
    if (m.kind === "note") lines.push(`- **Notiz ${label(m.title)}**${note}`);
    else if (m.kind === "link" && m.url) lines.push(`- Link [${label(m.title)}](${href(m.url)})${note}`);
    else if (m.repoFile) lines.push(`- [${label(m.title)}](${href(m.repoFile)})${note}`);
    else lines.push(`- ${label(m.title)} (bleibt in szyOS, größer als 1 MB)${note}`);
  }
  return `${lines.join("\n")}\n`;
}

/** Prüft, ob eine weitere Datei dazukommen darf. Gibt den Grund zurück, wenn nicht. */
export function materialLimitProblem(
  existing: { count: number; totalBytes: number },
  newBytes: number,
): string | null {
  if (existing.count >= MAX_MATERIAL_ITEMS) return `Höchstens ${MAX_MATERIAL_ITEMS} Einträge Material`;
  if (newBytes > MAX_MATERIAL_FILE_BYTES) return "Die Datei ist größer als 10 MB";
  if (existing.totalBytes + newBytes > MAX_MATERIAL_TOTAL_BYTES) return "Zusammen mehr als 50 MB Material";
  return null;
}
